import { Button, ButtonVariant, Text, TextField, Toggle } from "@merge-api/merge-javascript-shared";
import { Check, Plus, X } from "lucide-react";
import { EligibilityRule } from "../types";
import { SYNC_TYPE_OPTIONS } from "../helpers";

interface Props {
  rules: EligibilityRule[];
  onChange: (rules: EligibilityRule[]) => void;
}

export const emptyRule = (): EligibilityRule => ({
  sync_types: [],
  min_minutes_since_last_attempt: null,
  min_minutes_since_last_success: null,
  if_last_attempt_failed: null,
});

const DimLabel = ({ children }: { children: string }) => (
  <Text variant="sm" className="font-mono text-gray-70">
    {children}
  </Text>
);

const AddChip = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    className="rounded border border-dashed border-gray-20 px-2 py-0.5 font-mono text-xs text-gray-60 hover:border-gray-40 hover:text-gray-90"
  >
    + {label}
  </button>
);

const RemoveButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    onClick={onClick}
    aria-label={`Remove ${label}`}
    className="rounded p-1 text-gray-60 hover:bg-gray-10 hover:text-gray-90"
  >
    <X size={12} />
  </button>
);

// One AND-rule: a set of constraint dimensions that all must hold.
const RuleCard = ({
  rule,
  onChange,
  onRemove,
}: {
  rule: EligibilityRule;
  onChange: (patch: Partial<EligibilityRule>) => void;
  onRemove: () => void;
}) => {
  const hasSync = rule.sync_types.length > 0;
  const hasAttempt = rule.min_minutes_since_last_attempt != null;
  const hasSuccess = rule.min_minutes_since_last_success != null;
  const hasFailed = rule.if_last_attempt_failed != null;
  const anyDim = hasSync || hasAttempt || hasSuccess || hasFailed;

  return (
    <div className="flex flex-col gap-3 rounded-md border border-gray-10 bg-gray-0 p-3">
      <div className="flex items-center justify-between">
        <Text variant="sm" className="font-semibold uppercase tracking-wide text-gray-50">
          AND
        </Text>
        <RemoveButton label="rule" onClick={onRemove} />
      </div>

      {!anyDim && (
        <Text variant="sm" className="italic text-gray-60">
          No constraints yet. Add a dimension below.
        </Text>
      )}

      {hasSync && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <DimLabel>sync_types</DimLabel>
            <RemoveButton label="sync_types" onClick={() => onChange({ sync_types: [] })} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {SYNC_TYPE_OPTIONS.map((syncType) => {
              const on = rule.sync_types.includes(syncType);
              return (
                <button
                  key={syncType}
                  onClick={() =>
                    onChange({
                      sync_types: on
                        ? rule.sync_types.filter((x) => x !== syncType)
                        : [...rule.sync_types, syncType],
                    })
                  }
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-xs ${
                    on
                      ? "border-gray-40 bg-white text-gray-90"
                      : "border-gray-20 text-gray-60 hover:text-gray-90"
                  }`}
                >
                  {on && <Check size={11} />}
                  {syncType}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {hasAttempt && (
        <div className="flex items-center justify-between gap-3">
          <DimLabel>min minutes since last attempt</DimLabel>
          <div className="flex items-center gap-2">
            <div className="w-28">
              <TextField
                className="bg-white"
                type="number"
                value={String(rule.min_minutes_since_last_attempt ?? "")}
                onChange={(e) =>
                  onChange({ min_minutes_since_last_attempt: Number(e.target.value) })
                }
              />
            </div>
            <RemoveButton
              label="attempt cooldown"
              onClick={() => onChange({ min_minutes_since_last_attempt: null })}
            />
          </div>
        </div>
      )}

      {hasSuccess && (
        <div className="flex items-center justify-between gap-3">
          <DimLabel>min minutes since last success</DimLabel>
          <div className="flex items-center gap-2">
            <div className="w-28">
              <TextField
                className="bg-white"
                type="number"
                value={String(rule.min_minutes_since_last_success ?? "")}
                onChange={(e) =>
                  onChange({ min_minutes_since_last_success: Number(e.target.value) })
                }
              />
            </div>
            <RemoveButton
              label="success cooldown"
              onClick={() => onChange({ min_minutes_since_last_success: null })}
            />
          </div>
        </div>
      )}

      {hasFailed && (
        <div className="flex items-center justify-between gap-3">
          <DimLabel>if last attempt failed</DimLabel>
          <div className="flex items-center gap-2">
            <Toggle
              checked={!!rule.if_last_attempt_failed}
              onChange={(value) => onChange({ if_last_attempt_failed: value })}
            />
            <RemoveButton
              label="last-failed condition"
              onClick={() => onChange({ if_last_attempt_failed: null })}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 border-t border-dashed border-gray-10 pt-2">
        {!hasSync && (
          <AddChip label="sync_types" onClick={() => onChange({ sync_types: ["FULL"] })} />
        )}
        {!hasAttempt && (
          <AddChip
            label="min_minutes_since_last_attempt"
            onClick={() => onChange({ min_minutes_since_last_attempt: 60 })}
          />
        )}
        {!hasSuccess && (
          <AddChip
            label="min_minutes_since_last_success"
            onClick={() => onChange({ min_minutes_since_last_success: 60 })}
          />
        )}
        {!hasFailed && (
          <AddChip
            label="if_last_attempt_failed"
            onClick={() => onChange({ if_last_attempt_failed: true })}
          />
        )}
      </div>
    </div>
  );
};

// An OR-list of AND-rules. Empty list = the step is always eligible.
const EligibilityBuilder = ({ rules, onChange }: Props) => {
  const patchRule = (index: number, patch: Partial<EligibilityRule>) =>
    onChange(rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
  const removeRule = (index: number) => onChange(rules.filter((_, i) => i !== index));

  if (rules.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-gray-10 bg-gray-0 p-4 text-center">
        <Text variant="sm" className="text-gray-60">
          Empty: the step is always eligible.
        </Text>
        <Button
          size="sm"
          variant={ButtonVariant.SecondaryGray}
          leftIcon={<Plus size={14} />}
          onClick={() => onChange([emptyRule()])}
        >
          Add a rule to constrain it
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {rules.map((rule, index) => (
        <div key={index} className="flex flex-col gap-2">
          {index > 0 && (
            <Text variant="sm" className="font-mono uppercase text-gray-40">
              or
            </Text>
          )}
          <RuleCard
            rule={rule}
            onChange={(patch) => patchRule(index, patch)}
            onRemove={() => removeRule(index)}
          />
        </div>
      ))}
      <div>
        <Button
          size="sm"
          variant={ButtonVariant.SecondaryGray}
          leftIcon={<Plus size={14} />}
          onClick={() => onChange([...rules, emptyRule()])}
        >
          Add OR rule
        </Button>
      </div>
    </div>
  );
};

export default EligibilityBuilder;
